import React, { useState } from 'react';
import {
  AritoIcon,
  ExcelImportDialog,
  ExcelImportErrorDialog,
  DeleteDialog,
  ErpDataTableV2
} from '@/components/custom/erp';
import { ChinhSachChietKhauColumns, ChiTietChinhSachChietKhauColumns, getDataTableColumns } from './cols-definition';
import { toast, useExcel, useExcelProcessor, useCRUD, useForms } from '@/hooks';
import type { GiaBanV2, GiaBanV2Input } from '@/types/schemas';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { ActionBar, UpdatePriceDialog } from './components';
import { ExcelConfig, ImportBody } from './utils';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 30;

export default function DanhMucGiaBanPage() {
  const {
    data,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    searchData
  } = useCRUD<GiaBanV2, GiaBanV2Input>({
    endpoint: QUERY_KEYS.GIA_BAN,
    pageSize: PAGE_SIZE
  });

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);
  const [isLoadingImport, setIsLoadingImport] = useState(false);

  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();

  const {
    showForm,
    showDelete,
    formMode: mode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    isImportDialogOpen,
    setIsImportDialogOpen,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection,
    handleOpenImportExcel
  } = useForms<GiaBanV2>(fetchDetail);

  const handleImportBodies = async (bodies: ImportBody[]) => {
    let successCount = 0;
    setIsLoadingImport(true);
    try {
      for (const body of bodies) {
        await addItem(body);
        successCount++;
      }
      toast({
        description: 'Đã nhập danh mục giá bán từ Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      await refreshData();
      setIsLoadingImport(false);
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      getUUIDByCode,
      context: {}
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
    }
  };

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : data || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const tables = [
    {
      name: 'Cập nhật giá bán',
      rows: displayData,
      columns: getDataTableColumns()
    },
    {
      name: 'Chính sách chiết khấu',
      rows: [],
      columns: ChinhSachChietKhauColumns()
    },
    {
      name: 'Chi tiết chính sách chiết khấu',
      rows: [],
      columns: ChiTietChinhSachChietKhauColumns()
    }
  ];

  const handleFormSubmit = async (data: GiaBanV2Input) => {
    try {
      if (mode === 'add') {
        await addItem(data);
      } else if (mode === 'edit' && selectedObj?.uuid) {
        await updateItem(selectedObj.uuid, data);
      }

      refreshData();
      handleCloseForm();
      clearSelection();
    } catch (err) {
      return;
    }
  };

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <ActionBar
        onAddIconClick={handleAddClick}
        onEditIconClick={handleEditClick}
        onDeleteIconClick={() => selectedObj && handleDeleteClick()}
        onCopyIconClick={handleCopyClick}
        onWatchIconClick={handleViewClick}
        onImportFromExcel={handleOpenImportExcel}
        selectedObj={selectedObj}
        isEditDisabled={!selectedObj}
      />
      <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
          onTabChange={() => {
            clearSelection();
          }}
        />
      </div>

      {showForm && (
        <UpdatePriceDialog
          mode={mode}
          open={showForm}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onCopyButtonClick={handleCopyClick}
          onWatchButtonClick={handleViewClick}
          initialData={mode === 'add' && !isCopyMode ? undefined : detail}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deleteObj={deleteItem}
          clearSelection={clearSelection}
        />
      )}

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onSubmit={handleImportFromExcel}
          title='Lấy dữ liệu từ excel'
          titleIcon={<AritoIcon icon={555} />}
        />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorData(null);
          }}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}
    </div>
  );
}
